import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import { mockJobs, type Job, type JobStatus } from "@project/data/mockData";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Textarea } from "@project/components/ui/textarea";
import { Label } from "@project/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  List,
  LayoutGrid,
  Plus,
  Search,
  Eye,
  Sparkles,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Briefcase,
  MapPin,
  Users,
  BadgeCheck,
  XCircle,
  PauseCircle,
  Wand2,
  BarChart3,
  MessageSquareText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import { toast } from "@project/hooks/use-toast";
import { useAIAction } from "@project/hooks/useAIAction";
import AIResultDisplay from "@project/components/AIResultDisplay";

type ViewMode = "table" | "grid";
type FilterTab = "All" | JobStatus;

const statusConfig: Record<string, { icon: any; class: string }> = {
  Draft: { icon: FileText, class: "text-muted-foreground bg-muted" },
  "Pending Approval": {
    icon: Clock,
    class: "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  },
  Approved: {
    icon: BadgeCheck,
    class: "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]",
  },
  Active: {
    icon: CheckCircle2,
    class: "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]",
  },
  "On Hold": {
    icon: PauseCircle,
    class: "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]",
  },
  Filled: {
    icon: Users,
    class: "text-[hsl(var(--status-filled))] bg-[hsl(var(--status-filled-bg))]",
  },
  Closed: {
    icon: XCircle,
    class: "text-[hsl(var(--status-closed))] bg-[hsl(var(--status-closed-bg))]",
  },
};

const departments = [
  "Engineering",
  "Design",
  "Marketing",
  "Sales",
  "Finance",
  "HR",
  "Product",
  "Operations",
];

const JobsPage = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [activeFilter, setActiveFilter] = useState<FilterTab>("All");
  const [search, setSearch] = useState("");
  const [jobs, setJobs] = useState(mockJobs);
  const [createOpen, setCreateOpen] = useState(false);
  const [aiJdOpen, setAiJdOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiJdResult, setAiJdResult] = useState("");
  const [aiQuestionsOpen, setAiQuestionsOpen] = useState(false);
  const [aiQuestionsLoading, setAiQuestionsLoading] = useState(false);
  const [aiQuestionsResult, setAiQuestionsResult] = useState("");
  const [aiAnalysisOpen, setAiAnalysisOpen] = useState(false);
  const aiSkills = useAIAction();
  const aiAnalysis = useAIAction();
  const navigate = useNavigate();

  // New job form state
  const [newJob, setNewJob] = useState({
    title: "",
    department: "",
    experience: "",
    skills: "",
    budget: "",
    location: "",
    jobType: "Hybrid" as Job["jobType"],
    hiringManager: "",
    description: "",
    vacancies: 1,
  });

  const filterTabs: { label: FilterTab; count: number }[] = [
    { label: "All", count: jobs.length },
    { label: "Active", count: jobs.filter(j => j.status === "Active").length },
    {
      label: "Pending Approval",
      count: jobs.filter(j => j.status === "Pending Approval").length,
    },
    { label: "Draft", count: jobs.filter(j => j.status === "Draft").length },
    {
      label: "On Hold",
      count: jobs.filter(j => j.status === "On Hold").length,
    },
    { label: "Filled", count: jobs.filter(j => j.status === "Filled").length },
    { label: "Closed", count: jobs.filter(j => j.status === "Closed").length },
  ];

  const filtered = (
    activeFilter === "All" ? jobs : jobs.filter(j => j.status === activeFilter)
  ).filter(
    j =>
      !search ||
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      j.jobId.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreateJob = () => {
    if (!newJob.title || !newJob.department) {
      toast({
        title: "Required fields missing",
        description: "Title and Department are required.",
        variant: "destructive",
      });
      return;
    }
    const id = `j${Date.now()}`;
    const job: Job = {
      id,
      title: newJob.title,
      jobId: `JOB-${String(jobs.length + 1).padStart(3, "0")}`,
      department: newJob.department,
      location: newJob.location || "Remote",
      jobType: newJob.jobType,
      experience: newJob.experience || "2+ Years",
      skills: newJob.skills
        .split(",")
        .map(s => s.trim())
        .filter(Boolean),
      budget: newJob.budget || "TBD",
      hiringManager: newJob.hiringManager || "Not Assigned",
      status: "Draft",
      approvalStage: "Pending",
      vacancies: newJob.vacancies,
      applications: 0,
      shortlisted: 0,
      description: newJob.description,
      createdAt: new Date().toISOString().split("T")[0],
      expiryDate: "",
    };
    setJobs(prev => [job, ...prev]);
    setCreateOpen(false);
    setNewJob({
      title: "",
      department: "",
      experience: "",
      skills: "",
      budget: "",
      location: "",
      jobType: "Hybrid",
      hiringManager: "",
      description: "",
      vacancies: 1,
    });
    toast({
      title: "Job Created",
      description: `${job.title} saved as Draft.`,
    });
  };

  const handleApprove = (jobId: string) => {
    setJobs(prev =>
      prev.map(j => {
        if (j.id !== jobId) {
          return j;
        }
        if (j.approvalStage === "Pending") {
          return {
            ...j,
            approvalStage: "L1 Approved" as const,
            status: "Pending Approval" as const,
          };
        }
        if (j.approvalStage === "L1 Approved") {
          return { ...j, approvalStage: "L2 Approved" as const };
        }
        if (j.approvalStage === "L2 Approved") {
          return {
            ...j,
            approvalStage: "Final Approved" as const,
            status: "Approved" as const,
          };
        }
        return j;
      })
    );
    toast({ title: "Approval Updated" });
  };

  const handleGenerateJD = async () => {
    toast({
      title: "Feature Disabled",
      description:
        "AI Job Description generation is currently unavailable because Supabase has been removed.",
      variant: "destructive",
    });
  };

  const applyAiJd = () => {
    setNewJob(prev => ({ ...prev, description: aiJdResult }));
    setAiJdOpen(false);
    toast({
      title: "JD Applied",
      description: "AI-generated description applied to job form.",
    });
  };

  const handleGenerateQuestions = async () => {
    toast({
      title: "Feature Disabled",
      description:
        "AI Interview Question generation is currently unavailable because Supabase has been removed.",
      variant: "destructive",
    });
  };

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div
          variants={item}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="font-display text-lg font-bold tracking-tight text-foreground">
              Jobs
            </h1>
            <p className="mt-0.5 hidden text-[12px] text-muted-foreground sm:block">
              Manage requisitions, approvals, and job postings
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
              onClick={async () => {
                setAiAnalysisOpen(true);
                await aiAnalysis.run(`Analyze these job postings data and provide insights:
${jobs.map(j => `- ${j.title} (${j.department}): Status=${j.status}, Applications=${j.applications}, Approval=${j.approvalStage}`).join("\n")}

Provide:
1. **Job Pipeline Health** - overview of posting statuses
2. **Department Analysis** - which departments are hiring most
3. **Bottleneck Detection** - jobs stuck in approval or with low applications
4. **Optimization Tips** - suggestions to improve job postings
5. **Priority Actions** - what to focus on immediately`);
              }}
            >
              <BarChart3 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">AI Analysis</span>
            </Button>
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="btn-glow h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Create Requisition</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          variants={item}
          className="no-scrollbar flex items-center gap-0.5 overflow-x-auto rounded-xl bg-muted/50 p-0.5"
        >
          {filterTabs
            .filter(t => t.count > 0 || t.label === "All")
            .map(tab => (
              <button
                key={tab.label}
                onClick={() => setActiveFilter(tab.label)}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all duration-200 ${activeFilter === tab.label ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {tab.label}
                <span
                  className={`rounded-md px-1.5 py-0 text-[10px] font-bold ${activeFilter === tab.label ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
        </motion.div>

        {/* Search + View Toggle */}
        <motion.div
          variants={item}
          className="flex items-center justify-between gap-2"
        >
          <div className="relative max-w-[200px] flex-1 sm:max-w-xs">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search jobs..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-8 rounded-lg border-border/60 bg-card pl-8 text-[12px]"
            />
          </div>
          <div className="flex overflow-hidden rounded-lg border border-border/60 bg-card">
            <button
              onClick={() => setViewMode("table")}
              className={`p-1.5 transition-all ${viewMode === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-1.5 transition-all ${viewMode === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>

        {/* Table View */}
        <motion.div variants={item}>
          {filtered.length === 0 ? (
            <div className="section-card p-8 text-center">
              <p className="text-[13px] text-muted-foreground">
                No jobs found.
              </p>
            </div>
          ) : viewMode === "table" ? (
            <div className="section-card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="pl-5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Job
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Department
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Location
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Status
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Approval
                      </TableHead>
                      <TableHead className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Applications
                      </TableHead>
                      <TableHead className="w-[100px] text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map(job => {
                      const sc = statusConfig[job.status] || statusConfig.Draft;
                      return (
                        <TableRow key={job.id} className="table-row-hover">
                          <TableCell className="pl-5">
                            <div>
                              <p className="text-[13px] font-semibold text-foreground">
                                {job.title}
                              </p>
                              <p className="text-[11px] text-muted-foreground">
                                {job.jobId} · {job.experience}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-[12px] text-foreground">
                            {job.department}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-[12px] text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {job.location}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold ${sc.class}`}
                            >
                              <sc.icon className="h-3 w-3" />
                              {job.status}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-[11px] text-muted-foreground">
                              {job.approvalStage}
                            </span>
                          </TableCell>
                          <TableCell className="text-[12px] font-semibold text-foreground">
                            {job.applications}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => navigate(`/jobs/${job.id}`)}
                                className="rounded-lg p-1.5 transition-colors hover:bg-muted"
                              >
                                <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                              </button>
                              {job.approvalStage !== "Final Approved" && (
                                <button
                                  onClick={() => handleApprove(job.id)}
                                  className="rounded-lg p-1.5 transition-colors hover:bg-[hsl(var(--status-active-bg))]"
                                >
                                  <BadgeCheck className="h-3.5 w-3.5 text-[hsl(var(--status-active))]" />
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map(job => {
                const sc = statusConfig[job.status] || statusConfig.Draft;
                return (
                  <div
                    key={job.id}
                    className="section-card cursor-pointer space-y-3 p-4 transition-all hover:shadow-md"
                    onClick={() => navigate(`/jobs/${job.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-[14px] font-semibold text-foreground">
                          {job.title}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {job.jobId}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[10px] font-semibold ${sc.class}`}
                      >
                        <sc.icon className="h-3 w-3" />
                        {job.status}
                      </span>
                    </div>
                    <div className="space-y-1.5 text-[12px] text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {job.department}
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {job.location} · {job.jobType}
                      </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-border/30 pt-2">
                      <span className="text-[11px] text-muted-foreground">
                        {job.applications} applications
                      </span>
                      <span className="text-[11px] font-semibold text-foreground">
                        {job.vacancies} openings
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Create Job Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              New Job Requisition
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-[12px]">Job Title *</Label>
                <Input
                  value={newJob.title}
                  onChange={e =>
                    setNewJob(p => ({ ...p, title: e.target.value }))
                  }
                  placeholder="e.g. Senior UX Designer"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Department *</Label>
                <Select
                  value={newJob.department}
                  onValueChange={v => setNewJob(p => ({ ...p, department: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map(d => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Experience Required</Label>
                <Input
                  value={newJob.experience}
                  onChange={e =>
                    setNewJob(p => ({ ...p, experience: e.target.value }))
                  }
                  placeholder="e.g. 3+ Years"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[12px]">
                    Skills (comma separated)
                  </Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ai-btn-outline h-6 gap-1 rounded-md px-2 text-[10px]"
                    disabled={aiSkills.loading || !newJob.title}
                    onClick={async () => {
                      const result = await aiSkills.run(
                        `For the job role "${newJob.title}" in "${newJob.department || "General"}" department with "${newJob.experience || "any"}" experience, suggest 8-12 relevant technical and soft skills. Return ONLY a comma-separated list, nothing else.`
                      );
                      if (result) {
                        setNewJob(p => ({
                          ...p,
                          skills: p.skills
                            ? p.skills + ", " + result.trim()
                            : result.trim(),
                        }));
                      }
                    }}
                  >
                    {aiSkills.loading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Wand2 className="h-3 w-3" />
                    )}
                    AI Suggest
                  </Button>
                </div>
                <Input
                  value={newJob.skills}
                  onChange={e =>
                    setNewJob(p => ({ ...p, skills: e.target.value }))
                  }
                  placeholder="e.g. React, Node.js, SQL"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Budget / CTC Range</Label>
                <Input
                  value={newJob.budget}
                  onChange={e =>
                    setNewJob(p => ({ ...p, budget: e.target.value }))
                  }
                  placeholder="e.g. ₹20-30 LPA"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Location</Label>
                <Input
                  value={newJob.location}
                  onChange={e =>
                    setNewJob(p => ({ ...p, location: e.target.value }))
                  }
                  placeholder="e.g. Bengaluru"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Job Type</Label>
                <Select
                  value={newJob.jobType}
                  onValueChange={v =>
                    setNewJob(p => ({ ...p, jobType: v as any }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Remote">Remote</SelectItem>
                    <SelectItem value="On-site">On-site</SelectItem>
                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Hiring Manager</Label>
                <Input
                  value={newJob.hiringManager}
                  onChange={e =>
                    setNewJob(p => ({ ...p, hiringManager: e.target.value }))
                  }
                  placeholder="Manager name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[12px]">Vacancies</Label>
                <Input
                  type="number"
                  min={1}
                  value={newJob.vacancies}
                  onChange={e =>
                    setNewJob(p => ({
                      ...p,
                      vacancies: parseInt(e.target.value) || 1,
                    }))
                  }
                />
              </div>
            </div>

            {/* Job Description */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[12px]">Job Description</Label>
                <Button
                  size="sm"
                  className="ai-btn-outline h-7 gap-1 rounded-lg text-[11px]"
                  onClick={handleGenerateJD}
                >
                  <Sparkles className="h-3 w-3" />
                  Generate with AI
                </Button>
              </div>
              <Textarea
                value={newJob.description}
                onChange={e =>
                  setNewJob(p => ({ ...p, description: e.target.value }))
                }
                placeholder="Enter or paste job description, or click 'Generate with AI'..."
                rows={6}
              />
            </div>

            {/* Generate Questions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-[12px]">Screening Questions</Label>
                <Button
                  size="sm"
                  className="ai-btn h-7 gap-1 rounded-lg text-[11px]"
                  onClick={handleGenerateQuestions}
                >
                  <MessageSquareText className="h-3 w-3" />
                  Generate Questions
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">
                AI will generate HR screening questions based on the job role &
                skills.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateJob} className="btn-glow">
                Create Requisition
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI JD Dialog */}
      <Dialog open={aiJdOpen} onOpenChange={setAiJdOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Sparkles className="h-4 w-4 text-primary" />
              AI-Generated Job Description
            </DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Generating JD with AI...
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="max-h-[400px] overflow-y-auto whitespace-pre-wrap rounded-xl bg-muted/30 p-4 text-[13px] leading-relaxed text-foreground">
                {aiJdResult}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setAiJdOpen(false)}>
                  Discard
                </Button>
                <Button onClick={applyAiJd} className="btn-glow gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Apply to Form
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Job Analysis Dialog */}
      <Dialog open={aiAnalysisOpen} onOpenChange={setAiAnalysisOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <BarChart3 className="h-5 w-5 text-primary" />
              AI Job Pipeline Analysis
            </DialogTitle>
          </DialogHeader>
          {aiAnalysis.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Analyzing job postings...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiAnalysis.result} />
          )}
        </DialogContent>
      </Dialog>

      {/* AI Questions Dialog */}
      <Dialog open={aiQuestionsOpen} onOpenChange={setAiQuestionsOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <MessageSquareText className="h-5 w-5 text-primary" />
              AI-Generated Screening Questions
            </DialogTitle>
          </DialogHeader>
          {aiQuestionsLoading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Generating screening questions...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiQuestionsResult} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default JobsPage;
